/*
 * מה שמחבר את הלקוח לשרת האמיתי — ורק הוא.
 *
 * **כתובת ה-relay מגיעה מהסביבה ולא מהקוד.** ה-Worker נפרס בנפרד מהמשחק,
 * ותת-הדומיין שלו נקבע בחשבון Cloudflare ולא על ידינו. כתובת קשיחה כאן הייתה
 * הופכת כל פריסה של השרת לקומיט במשחק, וגם הייתה מחייבת לדעת אותה **לפני**
 * שהיא קיימת.
 *
 * **בלי הכתובת המולטיפלייר לא מוצע בכלל.** ‏`multiplayerEnabled()` מחזירה false,
 * והתפריט מסתיר את הכפתור. זה עדיף על כפתור שנראה תקין ונכשל בלחיצה — במיוחד
 * בבנייה שנפרסה לפני שהשרת עלה, שהיא בדיוק המצב הצפוי.
 */

/**
 * מזהה הלקוח יושב ב-`sessionStorage` ולא ב-`localStorage`, ולא בזיכרון.
 *
 * שני ההבדלים הם התנהגות שרצינו:
 *   - **לשונית = שחקן.** ‏`localStorage` משותף לכל הלשוניות, ואז שתי לשוניות
 *     באותו דפדפן היו נאבקות על אותו מושב — וזו בדיוק הדרך הנוחה ביותר לבדוק
 *     משחק רשת על מכונה אחת.
 *   - **רענון אינו מאבד את המושב.** מזהה בזיכרון בלבד היה משתנה בכל טעינה, וחיבור
 *     מחדש היה מקבל את המושב הפנוי של היריב במקום את שלנו.
 */
const CLIENT_ID_KEY = 'crimsonFront.clientId';

/** גיבוי כשאין אחסון (גלישה פרטית, ‏iframe חסום). עדיף מזהה חד-פעמי מאשר קריסה. */
let memoryClientId: string | null = null;

export function relayBaseUrl(): string | null {
    const raw: unknown = import.meta.env.VITE_RELAY_URL;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim().replace(/\/+$/, '');
    // רק http/https: מכאן נגזרות כתובת ה-API וכתובת ה-WebSocket גם יחד.
    if (!/^https?:\/\/[^\s]+$/.test(trimmed)) return null;
    return trimmed;
}

export function multiplayerEnabled(): boolean {
    return relayBaseUrl() !== null;
}

/**
 * מזהה יציב ללשונית.
 *
 * הפורמט מוגבל ל-`[A-Za-z0-9_-]` כי ה-relay פוסל כל דבר אחר (`isClientId`), והוא
 * גם נכנס למחרוזת שאילתה ולמפתחות אחסון בשרת. ‏`randomUUID` עומד בזה כמו שהוא.
 */
export function clientId(): string {
    try {
        const existing = sessionStorage.getItem(CLIENT_ID_KEY);
        if (existing !== null && /^[A-Za-z0-9_-]{1,64}$/.test(existing)) return existing;
        const fresh = newId();
        sessionStorage.setItem(CLIENT_ID_KEY, fresh);
        return fresh;
    } catch {
        memoryClientId ??= newId();
        return memoryClientId;
    }
}

function newId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // דפדפן ישן בלי `randomUUID`. אין כאן דרישת אבטחה — רק ייחודיות בין שתי לשוניות.
    return `c${Date.now().toString(36)}${Math.floor(Math.random() * 1e9).toString(36)}`;
}
